import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Wallet, CheckCircle, AlertCircle, DollarSign, Percent, Clock, Copy, ExternalLink, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

// Payment method icons (using FontAwesome CDN style or simple text)
const PayPalIcon = () => <span className="text-[#003087] font-bold">Pay<span className="text-[#009cde]">Pal</span></span>;
const CashAppIcon = () => <span className="text-[#00D632] font-bold">$Cash</span>;
const VenmoIcon = () => <span className="text-[#3D95CE] font-bold">Venmo</span>;
const StripeIcon = () => <span className="text-[#635BFF] font-bold flex items-center gap-1"><CreditCard className="w-4 h-4" />Card</span>;

export default function DepositPage() {
  const { user, updateUser } = useAuth();
  const { formatCurrency } = useCurrency();
  const [searchParams] = useSearchParams();
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [selectedInvestment, setSelectedInvestment] = useState('wallet');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [feeInfo, setFeeInfo] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  // Payment identifiers
  const [paypalEmail, setPaypalEmail] = useState('');
  const [cashtag, setCashtag] = useState('');
  const [venmoUsername, setVenmoUsername] = useState('');
  
  // Payment info
  const [paymentMethods, setPaymentMethods] = useState(null);

  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');
  const stripeSuccess = searchParams.get('stripe_success');
  const stripeSessionId = searchParams.get('session_id');
  const stripeCancelled = searchParams.get('stripe_cancelled');

  // Preset amounts
  const presetAmounts = [5, 15, 25, 50, 100, 250, 500, 1000];
  
  // Investment types
  const investmentTypes = [
    { id: 'wallet', name: 'Wallet', description: 'Add to your balance' },
    { id: 'small_business', name: 'Small Business', description: 'Invest in local businesses' },
    { id: 'stocks', name: 'Stocks', description: 'Trade stocks' },
    { id: 'crypto', name: 'Crypto', description: 'Trade cryptocurrency' },
    { id: 'metals', name: 'Metals', description: 'Trade precious metals' }
  ];

  // Load payment methods
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const res = await api.get('/payments/methods');
        setPaymentMethods(res.data);
      } catch (error) {
        console.error('Failed to load payment methods:', error);
      }
    };
    fetchPaymentMethods();
  }, []);

  // Handle PayPal return
  useEffect(() => {
    if (paymentId && payerId) {
      executePayPalPayment(paymentId, payerId);
    }
  }, [paymentId, payerId]);

  // Handle Stripe return
  useEffect(() => {
    if (stripeSuccess === 'true' && stripeSessionId) {
      verifyStripePayment(stripeSessionId);
    } else if (stripeCancelled === 'true') {
      toast.info('Payment cancelled. No charges were made.');
    }
  }, [stripeSuccess, stripeSessionId, stripeCancelled]);

  const verifyStripePayment = async (sessionId) => {
    setLoading(true);
    try {
      const res = await api.get(`/stripe/verify-session/${sessionId}`);
      if (res.data.success) {
        setPaymentStatus({ success: true, method: 'stripe', ...res.data });
        const userRes = await api.get('/auth/me');
        updateUser(userRes.data);
        toast.success(`Deposit successful! $${res.data.amount} added to your account.`);
      }
    } catch (error) {
      setPaymentStatus({ success: false, error: error.response?.data?.detail || 'Payment verification failed' });
      toast.error('Payment verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Calculate fees when amount changes
  useEffect(() => {
    if (amount && parseFloat(amount) >= 5) {
      calculateFee();
    } else {
      setFeeInfo(null);
    }
  }, [amount]);

  const calculateFee = async () => {
    try {
      const res = await api.get(`/paypal/fee-calculator?amount=${amount}`);
      setFeeInfo({
        amount: parseFloat(amount),
        fee: res.data.fee,
        fee_percent: res.data.fee_percent,
        net_amount: res.data.net_amount
      });
    } catch (error) {
      console.error('Fee calculation error:', error);
    }
  };

  const executePayPalPayment = async (paymentId, payerId) => {
    setLoading(true);
    try {
      const res = await api.post('/paypal/execute-payment', { payment_id: paymentId, payer_id: payerId });
      if (res.data.success) {
        setPaymentStatus({ success: true, ...res.data });
        const userRes = await api.get('/auth/me');
        updateUser(userRes.data);
        toast.success(`Deposit successful! $${res.data.amount} added to your account.`);
      }
    } catch (error) {
      setPaymentStatus({ success: false, error: error.response?.data?.detail || 'Payment failed' });
      toast.error('Payment execution failed');
    } finally {
      setLoading(false);
    }
  };

  // Stripe Deposit
  const handleStripeDeposit = async () => {
    const depositAmount = parseFloat(amount);
    if (!depositAmount || depositAmount < 5) {
      toast.error('Minimum deposit is $5');
      return;
    }
    if (depositAmount > 1000) {
      toast.error('Maximum deposit is $1,000');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/stripe/create-checkout-session', { 
        amount: depositAmount,
        investment_type: selectedInvestment
      });
      if (res.data.success && res.data.checkout_url) {
        toast.info('Redirecting to secure checkout...');
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    } finally {
      setLoading(false);
    }
  };

  // PayPal Deposit
  const handlePayPalDeposit = async () => {
    const depositAmount = parseFloat(amount);
    if (!depositAmount || depositAmount < 5) {
      toast.error('Minimum deposit is $5');
      return;
    }
    if (depositAmount > 1000) {
      toast.error('Maximum deposit is $1,000');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/paypal/create-payment', { amount: depositAmount });
      if (res.data.success && res.data.approval_url) {
        toast.info('Redirecting to PayPal...');
        window.location.href = res.data.approval_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    } finally {
      setLoading(false);
    }
  };

  // Cash App Deposit
  const handleCashAppDeposit = async () => {
    const depositAmount = parseFloat(amount);
    if (!depositAmount || depositAmount < 5) {
      toast.error('Minimum deposit is $5');
      return;
    }
    if (!cashtag || !cashtag.startsWith('$')) {
      toast.error('Please enter a valid Cash App $cashtag (must start with $)');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/payments/cashapp/deposit', {
        cashtag,
        amount: depositAmount,
        investment_type: selectedInvestment
      });
      if (res.data.success) {
        setPaymentStatus({
          success: true,
          pending: true,
          method: 'cashapp',
          ...res.data
        });
        toast.success('Deposit request created! Please send payment via Cash App.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deposit request');
    } finally {
      setLoading(false);
    }
  };

  // Venmo Deposit
  const handleVenmoDeposit = async () => {
    const depositAmount = parseFloat(amount);
    if (!depositAmount || depositAmount < 5) {
      toast.error('Minimum deposit is $5');
      return;
    }
    if (!venmoUsername || !venmoUsername.startsWith('@')) {
      toast.error('Please enter a valid Venmo username (must start with @)');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/payments/venmo/deposit', {
        venmo_username: venmoUsername,
        amount: depositAmount,
        investment_type: selectedInvestment
      });
      if (res.data.success) {
        setPaymentStatus({
          success: true,
          pending: true,
          method: 'venmo',
          ...res.data
        });
        toast.success('Deposit request created! Please send payment via Venmo.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deposit request');
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = () => {
    switch (selectedMethod) {
      case 'stripe':
        handleStripeDeposit();
        break;
      case 'paypal':
        handlePayPalDeposit();
        break;
      case 'cashapp':
        handleCashAppDeposit();
        break;
      case 'venmo':
        handleVenmoDeposit();
        break;
      default:
        toast.error('Please select a payment method');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };


  // Payment Success/Pending UI
  if (paymentStatus) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className={paymentStatus.success ? 'border-green-500' : 'border-red-500'}>
            <CardContent className="p-8 text-center">
              {paymentStatus.success ? (
                <>
                  {paymentStatus.pending ? (
                    <>
                      <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                      <h2 className="text-2xl font-bold mb-2">Payment Pending Verification</h2>
                      <p className="text-muted-foreground mb-6">
                        Please complete the payment using the instructions below
                      </p>
                      
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-6 text-left">
                        <h3 className="font-semibold mb-4">Send Payment To:</h3>
                        {paymentStatus.method === 'cashapp' && (
                          <div className="flex items-center justify-between mb-2">
                            <span>Cash App:</span>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-[#00D632]">{paymentStatus.sraivest_cashtag}</span>
                              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(paymentStatus.sraivest_cashtag)}>
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                        {paymentStatus.method === 'venmo' && (
                          <div className="flex items-center justify-between mb-2">
                            <span>Venmo:</span>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-[#3D95CE]">{paymentStatus.sraivest_venmo}</span>
                              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(paymentStatus.sraivest_venmo)}>
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-2">
                          <span>Amount:</span>
                          <span className="font-bold">${paymentStatus.gross_amount}</span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span>Include in Note:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{user?.email}</span>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(user?.email)}>
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-sm text-muted-foreground mb-4">
                        <p>✓ Funds will be credited after admin verification</p>
                        <p>✓ Usually within 1 hour during business hours</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
                      <p className="text-3xl font-bold text-green-500 mb-4">
                        +${paymentStatus.amount || paymentStatus.net_amount}
                      </p>
                    </>
                  )}
                  <Button onClick={() => setPaymentStatus(null)} className="mt-4">
                    Make Another Deposit
                  </Button>
                </>
              ) : (
                <>
                  <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
                  <p className="text-muted-foreground mb-4">{paymentStatus.error}</p>
                  <Button onClick={() => setPaymentStatus(null)}>Try Again</Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Deposit Funds</h1>
            <p className="text-muted-foreground">Add money to your Sraivest account</p>
          </div>
          <Card className="px-4 py-2">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Balance:</span>
              <span className="font-bold">{formatCurrency(user?.balance || 0)}</span>
            </div>
          </Card>
        </div>

        {/* Payment Method Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Select Payment Method
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedMethod} onValueChange={setSelectedMethod}>
              <TabsList className="grid grid-cols-4 w-full mb-6">
                <TabsTrigger value="stripe" className="flex items-center gap-2" data-testid="stripe-tab">
                  <StripeIcon />
                  <Badge variant="outline" className="ml-1 text-xs bg-purple-100 text-purple-700">LIVE</Badge>
                </TabsTrigger>
                <TabsTrigger value="cashapp" className="flex items-center gap-2" data-testid="cashapp-tab">
                  <CashAppIcon />
                </TabsTrigger>
                <TabsTrigger value="venmo" className="flex items-center gap-2" data-testid="venmo-tab">
                  <VenmoIcon />
                </TabsTrigger>
              </TabsList>

              {/* Stripe Tab */}
              <TabsContent value="stripe" className="space-y-4">
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-[#635BFF]" />
                  <div>
                    <p className="font-medium">Credit/Debit Card</p>
                    <p className="text-sm text-muted-foreground">Secure payment via Stripe - Instant deposits</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Visa, Mastercard, American Express, Discover accepted</span>
                </div>
              </TabsContent>


              {/* Cash App Tab */}
              <TabsContent value="cashapp" className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="font-medium">Manual Verification</p>
                      <p className="text-sm text-muted-foreground">Funds credited after admin confirms payment (usually within 1 hour)</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Send payment to:</p>
                    <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded p-3">
                      <span className="text-xl font-bold text-[#00D632]">$Sraivest</span>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard('$Sraivest')}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Your Cash App $cashtag</label>
                  <Input
                    placeholder="$YourCashtag"
                    value={cashtag}
                    onChange={(e) => setCashtag(e.target.value)}
                    data-testid="cashtag-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Must start with $ (e.g., $JohnDoe)</p>
                </div>
              </TabsContent>

              {/* Venmo Tab */}
              <TabsContent value="venmo" className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="font-medium">Manual Verification</p>
                      <p className="text-sm text-muted-foreground">Funds credited after admin confirms payment (usually within 1 hour)</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Send payment to:</p>
                    <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded p-3">
                      <span className="text-xl font-bold text-[#3D95CE]">@Sraivest</span>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard('@Sraivest')}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Your Venmo Username</label>
                  <Input
                    placeholder="@YourUsername"
                    value={venmoUsername}
                    onChange={(e) => setVenmoUsername(e.target.value)}
                    data-testid="venmo-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Must start with @ (e.g., @JaneDoe)</p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Investment Type Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Where to Invest?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {investmentTypes.map((type) => (
                <Button
                  key={type.id}
                  variant={selectedInvestment === type.id ? 'default' : 'outline'}
                  className="h-auto py-3 flex flex-col"
                  onClick={() => setSelectedInvestment(type.id)}
                  data-testid={`investment-${type.id}`}
                >
                  <span className="font-medium">{type.name}</span>
                  <span className="text-xs opacity-70">{type.description}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Amount Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Deposit Amount</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {presetAmounts.map((preset) => (
                <Button
                  key={preset}
                  variant={parseFloat(amount) === preset ? 'default' : 'outline'}
                  onClick={() => setAmount(preset.toString())}
                  data-testid={`amount-${preset}`}
                >
                  ${preset}
                </Button>
              ))}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2">Custom Amount</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-8"
                    min="5"
                    max="1000"
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>
            </div>

            {/* Fee Display */}
            {feeInfo && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deposit Amount:</span>
                  <span>${feeInfo.amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-yellow-600">
                  <span className="flex items-center gap-1">
                    <Percent className="w-4 h-4" />
                    Processing Fee ({feeInfo.fee_percent}%):
                  </span>
                  <span>-${feeInfo.fee.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>You'll Receive:</span>
                  <span className="text-green-500">${feeInfo.net_amount.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Deposit Button */}
            <Button
              className="w-full h-12 text-lg"
              onClick={handleDeposit}
              disabled={loading || !amount || parseFloat(amount) < 5}
              data-testid="deposit-button"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Deposit ${amount || '0'} via {
                    selectedMethod === 'stripe' ? 'Card' : 
                    selectedMethod === 'cashapp' ? 'Cash App' : 'Venmo'
                  }
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-purple-50 via-blue-50 to-green-50 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-green-900/20">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-3">Deposit Information</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>✓ <strong>Credit/Debit Card (Stripe):</strong> Instant deposits - secure checkout</li>
              <li>✓ <strong>Cash App / Venmo:</strong> Manual verification - usually within 1 hour</li>
              <li>✓ Minimum deposit: $5</li>
              <li>✓ Maximum deposit: $1,000</li>
              <li>✓ Fees: 2% (under $100), 5% ($100-$999), 10% ($1000+)</li>
              <li>✓ All transactions are secure and encrypted</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
